<template>
  <div class="sticky-nav" :class="{ 'fixed': isFixed }" ref="nav">
    <div class="box">
      <ul>
        <li v-for="(item, index) in navItems" :key="item.id">
          <a :href="'#' + item.id" @click.prevent="scrollTo(item.id, index)" :class="{ 'active': activeIndex === index }">{{ item.title }}</a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StickyNav',
  props: {
    navItems: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isFixed: false,
      offsetTop: 0,
      activeIndex: 0,
      sectionTops: [],
      navHeight: 0,
      userIsScrolling: false // Flag to prevent scroll handler from overriding clicked item
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.offsetTop = this.$el.offsetTop;
      this.navHeight = this.$refs.nav.offsetHeight;
      this.calculateSectionTops();
    });
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    navItems() {
      this.$nextTick(() => {
        this.calculateSectionTops();
      });
    }
  },
  methods: {
    calculateSectionTops() {
      this.sectionTops = this.navItems.map(item => {
        const element = document.getElementById(item.id);
        if (element) {
          // Use getBoundingClientRect for a more accurate position relative to the document
          return element.getBoundingClientRect().top + window.pageYOffset;
        }
        return 0;
      }).filter(top => top > 0);
    },
    handleResize() {
      // Recalculate on resize as positions might change
      this.calculateSectionTops();
      this.navHeight = this.$refs.nav.offsetHeight;
    },
    handleScroll() {
      const scrollPosition = window.pageYOffset;
      this.isFixed = scrollPosition > this.offsetTop;

      if (this.userIsScrolling) {
        return;
      }

      // Update activeIndex based on scroll position
      let currentSectionIndex = this.activeIndex;
      const scrollThreshold = this.navHeight + 50; // nav height + 50px buffer

      for (let i = 0; i < this.sectionTops.length; i++) {
        if (scrollPosition < this.sectionTops[i] - scrollThreshold) {
          currentSectionIndex = i > 0 ? i - 1 : 0;
          break;
        }
        currentSectionIndex = i;
      }
      this.activeIndex = currentSectionIndex;
    },
    scrollTo(id, index) {
      this.activeIndex = index;
      const element = document.getElementById(id);
      if (element) {
        const top = element.getBoundingClientRect().top + window.pageYOffset - this.navHeight - 30;

        this.userIsScrolling = true; // Set flag
        window.scrollTo({ top, behavior: 'smooth' });

        // Unset the flag after scrolling is likely complete
        setTimeout(() => {
          this.userIsScrolling = false;
        }, 1000); // 1 second should be enough for smooth scroll to finish
      }
    }
  }
};
</script>


<style scoped>
.sticky-nav {
  background: #fff;
  border-bottom: 1px solid #eee;
  padding: 15px 0;
  transition: all 0.3s ease;
  z-index: 999;
}

.sticky-nav.fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.sticky-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  justify-content: center;
  gap: 40px;
}

.sticky-nav a {
  text-decoration: none;
  color: #333;
  font-weight: 500;
  padding: 10px 0;
  position: relative;
  transition: color 0.3s ease;
}

.sticky-nav a::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: #47975a;
  transition: width 0.3s ease;
}

.sticky-nav a:hover,
.sticky-nav a.active {
  color: #47975a;
}

.sticky-nav a:hover::after,
.sticky-nav a.active::after {
  width: 100%;
}
</style>